import {
  FieldMetaProps,
  FormikConfig,
  FormikHelpers,
  FormikValues,
  getIn,
  setIn,
  useFormik,
} from "formik";
import { useState } from "react";
import * as Yup from "yup";

export type FormWithSchema<S extends Yup.AnySchema> = FormikHookProps<
  Yup.TypeOf<S>
> &
  Submits<Yup.Asserts<S>> & {
    field: SubscriptableFieldProps<Yup.TypeOf<S>>;
    validationSchema: S;
    updateInitialArgsAndReset: () => void;
  };

export function useFormWithSchema<S extends Yup.AnySchema>({
  validationSchema,
  onSubmit,
  ...config
}: Omit<FormikConfig<Yup.TypeOf<S>>, "validationSchema" | "onSubmit"> & {
  validationSchema: S;
  onSubmit: (
    values: Yup.Asserts<S>,
    formikHelpers: FormikHelpers<Yup.TypeOf<S>>,
  ) => void | Promise<any>;
}): FormWithSchema<S> {
  type V = Yup.TypeOf<S>;
  const formik = useFormik({
    validationSchema,
    onSubmit: (values, helpers) => {
      return onSubmit(validationSchema.cast(values) as Yup.Asserts<S>, helpers);
    },
    ...config,
  });
  return {
    ...formik,
    validationSchema,
    field: addSubscriptOperator<V, BaseSubscriptableFieldProps<V>>({
      name: "",
      value: formik.values,
      onChange: async values => {
        await formik.setValues(values);
      },
      getSubField: key => getFieldProps({ ...formik, validationSchema }, key),
    }),
    updateInitialArgsAndReset: () => {
      formik.resetForm({
        values: config.initialValues,
        touched: config.initialTouched,
        errors: config.initialErrors,
        status: config.initialStatus,
      });
    },
  };
}

export type Submits<V> = { __unused?: V };
export type GetSubmitType<T> = T extends (...arg: never) => Submits<infer V>
  ? V
  : never;
export type GetFormValues<T> = T extends (
  ...arg: never
) => FormikHookProps<infer V>
  ? V
  : never;

export interface SimpleFieldProps<V> {
  name: string;
  value: V;
  error?: string;
  onFocus?: (value: V) => Promise<void>;
  onBlur?: (
    value: V,
    initialValue: V,
    shouldValidate?: boolean,
  ) => Promise<void>;
  onChange: (value: V, shouldValidate?: boolean) => Promise<void>;
  onError?: (error: string | undefined) => Promise<void>;
}

interface BaseSubscriptableFieldProps<V> extends SimpleFieldProps<V> {
  getSubField: <K extends keyof V>(key: K) => FormikFieldProps<V[K]>;
}

export type SubscriptableFieldProps<V> = BaseSubscriptableFieldProps<V> &
  Subscriptable<V>;

function addSubscriptOperator<V, F extends BaseSubscriptableFieldProps<V>>(
  field: F,
) {
  return new Proxy(field, {
    get(obj, prop) {
      if (prop in obj) {
        return obj[prop as keyof F];
      } else {
        return field.getSubField(prop as keyof V);
      }
    },
  }) as F & Subscriptable<V>;
}

type Subscriptable<V> = {
  [K in string & keyof V]: FormikFieldProps<V[K]>;
};

interface BaseFormikFieldProps<V> extends BaseSubscriptableFieldProps<V> {
  onChange: (value: V, shouldValidate?: boolean | undefined) => Promise<void>;
  touched: boolean;
  setTouched: (value: boolean) => Promise<void>;
  onBlur: (
    value: V,
    initialValue: V,
    shouldValidate?: boolean | undefined,
  ) => Promise<void>;
  blurCount: number;
  register: (input: {
    validate: (value: V) => string | void | Promise<string | void>;
  }) => void;
  unregister: () => void;

  getLabelFromSchema: () => {
    label: React.ReactNode;
    isRequired: boolean;
    htmlFor: string;
  };
}

export type FormikFieldProps<V> = BaseFormikFieldProps<V> & Subscriptable<V>;

export function useSimpleFieldProps<V>(
  field: SimpleFieldProps<V> | BaseFormikFieldProps<V>,
) {
  const [initialValue, setInitialValue] = useState<V | null>(null);
  return {
    ...field,
    initialValue: field.value,
    onFocus: async (value: V) => {
      setInitialValue(value);
    },
    onChange: async (value: V, shouldValidate?: boolean | undefined) => {
      await field.onChange?.(value, shouldValidate);
    },
    onBlur: async (value: V, shouldValidate?: boolean | undefined) => {
      await field.onBlur?.(value, initialValue!, shouldValidate);
    },
  };
}

export function getErrorMessageFromMeta(meta: FieldMetaProps<any>) {
  const initialError =
    meta.value === meta.initialValue ? meta.initialError : undefined;
  const error = (meta.touched ? meta.error : undefined) ?? initialError;
  // NOTE: error can be an object/array in case we use a parent fieldName to fetch the error
  // the errors in those cases are usually handled by the fieldName.subFieldName inputs, so we ignore them
  return typeof error === "string" ? error : undefined;
}

export interface Converter<V, Str = string> {
  parse: (value: Str) => V;
  format: (value: V) => Str;
}

export function withFieldValueConverter<V, Str>(
  field: SimpleFieldProps<V>,
  { format, parse }: Converter<V, Str>,
): SimpleFieldProps<Str> {
  return {
    ...field,
    value: format(field.value),
    onFocus: async initialValue => {
      await field.onFocus?.(parse(initialValue));
    },
    onBlur: async (value, initialValue, shouldValidate) => {
      await field.onBlur?.(parse(value), parse(initialValue), shouldValidate);
    },
    onChange: async (value, shouldValidate) => {
      await field.onChange?.(parse(value), shouldValidate);
    },
  };
}

function getFieldPropsRec<V>(
  formik: FormikPropsCovariant<any>,
  fieldName: string,
  options?: {
    onBlur?: (value: V) => void;
    onBlurChanged?: (value: V, initialValue: V) => void;
  },
): FormikFieldProps<V> {
  const field = formik.getFieldProps({ name: fieldName });
  const meta = formik.getFieldMeta(fieldName);
  const blurCountStatus = formik.status?.blurCounts ?? {};
  const blurCount = (getIn(blurCountStatus, fieldName) as number) ?? 0;
  return addSubscriptOperator<V, BaseFormikFieldProps<V>>({
    name: field.name,
    value: field.value,
    error: getErrorMessageFromMeta(meta),
    onChange: async (value, shouldValidate) => {
      await formik.setFieldValue(fieldName, value, shouldValidate);
    },
    onError: async error => {
      formik.setFieldError(fieldName, error);
    },
    onFocus: async () => undefined,
    onBlur: async (value, initialValue, shouldValidate) => {
      await formik.setFieldTouched(fieldName, true, shouldValidate);
      options?.onBlur?.(value);
      if (initialValue !== value) {
        options?.onBlurChanged?.(value, initialValue);
        formik.setStatus({
          blurCounts: setIn(blurCountStatus, fieldName, blurCount + 1),
        });
      }
    },
    blurCount,
    getSubField: key =>
      getFieldPropsRec(formik, `${fieldName}.${key as string}`),
    register: input => {
      formik.registerField(fieldName, input);
    },
    unregister: () => {
      formik.unregisterField(fieldName);
    },
    touched: meta.touched,
    setTouched: async value => {
      await formik.setFieldTouched(fieldName, value);
    },

    getLabelFromSchema: () => {
      const subSchema = Yup.reach(
        formik.validationSchema,
        field.name,
      ) as Yup.AnySchema;
      return {
        label: subSchema.spec.label,
        isRequired: subSchema.spec.presence === "required",
        htmlFor: field.name,
      };
    },
  });
}

function getFieldProps<Values extends FormikValues, K extends keyof Values>(
  formik: FormikPropsCovariant<Values>,
  fieldName: K,
  options?: {
    onBlur?: (value: Values[K]) => void;
    onBlurChanged?: (value: Values[K], initialValue: Values[K]) => void;
  },
) {
  return getFieldPropsRec(formik, fieldName as string, options);
}

class FormikHookPropsHelper<V extends FormikValues> {
  useFormik = (args: FormikConfig<V>) => useFormik<V>(args);
}
export type FormikHookProps<V extends FormikValues> = ReturnType<
  FormikHookPropsHelper<V>["useFormik"]
>;

// NOTE: covariant means if A can be converted to B then FromikPropsCovariant<A> can be converted to FromikPropsCovariant<B>
// this lets us handle part of a form with FormikPropsCovariant<SubFormValues>, which is not possible with FormikProps
type FormikPropsCovariant<V extends FormikValues> = Pick<
  FormikHookProps<V>,
  | "values"
  | "getFieldProps"
  | "setFieldValue"
  | "setFieldError"
  | "setFieldTouched"
  | "status"
  | "setStatus"
  | "getFieldMeta"
  | "registerField"
  | "unregisterField"
> & {
  validationSchema: Yup.AnySchema;
};

export function createFieldProps<V>(options: {
  name: string;
  value: V;
  setValue: (input: V) => void;
}): SimpleFieldProps<V> {
  return {
    name: options.name,
    value: options.value,
    onChange: async value => options.setValue(value),
  };
}
